// v10/src/components/PilotFlightLog.js
// FIX i18n: useTranslation hook agregado al componente principal
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, BookOpen, Award, BarChart3, FileDown, FileText, Table2, Star, Wallet, AlertTriangle, Upload as UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PeriodPicker from './PeriodPicker';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                 'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// PR #5 — Libro de vuelo: labels legibles
const PURPOSE_LABELS = {
  ENT: 'Entrenamiento', VP: 'Vuelo personal', ADAP: 'Adaptación',
  READAP: 'Readaptación', INST: 'Instrucción',
  Bautismo: 'Bautismo', Excursion: 'Excursión', Otros: 'Otros',
};
const purposeLabel = (e) => {
  if (!e?.purpose) return '—';
  if (e.purpose === 'Otros') return e.purpose_other ? `Otros: ${e.purpose_other}` : 'Otros';
  return PURPOSE_LABELS[e.purpose] || e.purpose;
};
const instructorLabel = (e) => e?.instructor_name || '—';

const PilotFlightLog = ({ darkMode, onClose, currentUser }) => {
  const { t } = useTranslation();
  const now   = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [data, setData]     = useState(null);
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState('flights');

  // PR #6 — Saldo de paquetes de horas del alumno
  // PR #6 (Vision) — filtro para mostrar/ocultar vuelos importados de planillas
  const [showImported, setShowImported] = useState(true);

  const [pkgBalance, setPkgBalance] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get('/api/hour-packages/my-balance')
      .then(r => { if (!cancelled) setPkgBalance(r.data?.data || null); })
      .catch(() => { if (!cancelled) setPkgBalance(null); });
    return () => { cancelled = true; };
  }, []);

  // Horas previas
  const [prevHours, setPrevHours]     = useState(parseFloat(currentUser?.previous_flight_hours || 0));
  const [editingPrev, setEditingPrev] = useState(false);
  const [prevInput, setPrevInput]     = useState('');
  const [savingPrev, setSavingPrev]   = useState(false);
  const [savedOk, setSavedOk]         = useState(false);
  const [saveError, setSaveError]     = useState('');

  // ✅ Fetchear horas previas frescas desde el servidor al abrir el modal
  useEffect(() => {
    if (currentUser?.id) {
      api.get(`/api/users/${currentUser.id}`)
        .then(r => {
          const hrs = parseFloat(
            r.data?.data?.previous_flight_hours ??
            r.data?.previous_flight_hours ??
            0
          );
          console.log('[PilotFlightLog] previous_flight_hours desde DB:', hrs);
          setPrevHours(hrs);
        })
        .catch(err => {
          console.error('[PilotFlightLog] Error fetching user:', err?.response?.data || err.message);
        });
    }
  }, [currentUser?.id]);

  useEffect(() => {
    setLoading(true);
    // Pedimos también abiertos y anulados para mostrarlos diferenciados.
    // Los totales del backend siempre se calculan SOLO sobre cerrados.
    api.get(`/api/reports/flight-log?year=${year}&month=${month}&include_open=true&include_annulled=true`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    api.get(`/api/reports/pilot-stats?year=${year}`)
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
  }, [year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isCurrent) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;

  // PR #6 (Vision): los vuelos importados se identifican con e.import_id.
  // Si el filtro está OFF, los ocultamos del listado y de los totales del mes.
  const myEntries = (data?.entries || [])
    .filter(e => !currentUser || e.pilot_id === currentUser.id)
    .filter(e => showImported || !e.import_id);
  // Sólo los CERRADOS cuentan para horas / totales / exports (abiertos y anulados se
  // muestran diferenciados pero no suman). Si la entry no trae status (backend viejo),
  // la tratamos como cerrada por retrocompat.
  const myClosedEntries = myEntries.filter(e => (e.status ?? 'closed') === 'closed' && e.hours_flown != null);
  const myHours    = myClosedEntries.reduce((a, e) => a + parseFloat(e.hours_flown || 0), 0);
  const totalHours = parseFloat(stats?.career?.total_hours || 0) + prevHours;

  const maxBarHours = stats ? Math.max(...stats.monthly.map(m => m.hours), 1) : 1;

  const savePrevHours = async () => {
    const val = parseFloat(prevInput);
    console.log('[PilotFlightLog] Intentando guardar horas previas:', val);
    if (isNaN(val) || val < 0) {
      setSaveError('Ingresá un número válido mayor o igual a 0');
      return;
    }
    setSavingPrev(true);
    setSaveError('');
    try {
      console.log('[PilotFlightLog] PATCH /api/users/me/previous-hours →', val);
      const res = await api.patch('/api/users/me/previous-hours', { previous_flight_hours: val });
      console.log('[PilotFlightLog] Respuesta:', res.data);
      setPrevHours(val);
      setEditingPrev(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Error al guardar';
      console.error('[PilotFlightLog] Error al guardar horas previas:', err?.response?.data || err.message);
      setSaveError(msg);
    } finally {
      setSavingPrev(false);
    }
  };

  const handleExportCSV = () => {
    if (!myClosedEntries.length) return;
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = 'Fecha,Aeronave,Matrícula,Horómetro Inicio,Horómetro Fin,Horas,Finalidad,Instructor,Aterrizajes,Forma de pago,Club\n';
    const rows = myClosedEntries.map(e => [
      e.flight_date, e.aircraft, e.registration || '',
      e.hobbs_start, e.hobbs_end, e.hours_flown,
      purposeLabel(e), instructorLabel(e),
      e.landings != null ? e.landings : '',
      e.payment_method || '',
      e.club_name || '',
    ].map(esc).join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mis-vuelos-${year}-${String(month).padStart(2,'0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    if (!myClosedEntries.length) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pilotName = currentUser?.name || 'Piloto';
    const monthLabel = `${MONTHS[month-1]} ${year}`;
    const orgName = currentUser?.organization_name
      || myClosedEntries.find(e => e.organization_name)?.organization_name
      || myClosedEntries.find(e => e.club_name)?.club_name
      || 'Condor360';
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // ── Membrete: aeroclub como título principal ─────────────────
    const NAVY = [15, 40, 80];
    const BLUE = [37, 99, 235];
    const GRAY = [100, 116, 139];
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, pageWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.text(`${orgName} — Libro de Vuelos`, 14, 9);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(147, 197, 253);
    doc.text(`Generado por Condor360 · ${new Date().toLocaleDateString('es-AR')}`, 14, 15);

    // ── Metadata: piloto / período / total ───────────────────────
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Piloto: ${pilotName}`, 14, 28);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`Período: ${monthLabel}`, 14, 33);
    doc.text(`Total: ${myClosedEntries.length} vuelos · ${myHours.toFixed(1)} hs`, 14, 38);

    // ── Tabla — anchos calculados para evitar truncado ───────────
    // landscape A4 = 297mm. Margen 12mm cada lado ⇒ 273mm útiles.
    // Anchos elegidos para que Forma de pago, Instructor y Aeroclub se vean completos.
    const colsDef = [
      { key: '#',          w: 8,  align: 'left'  },
      { key: 'Fecha',      w: 22, align: 'left'  },
      { key: 'Aeronave',   w: 36, align: 'left'  },
      { key: 'Matrícula',  w: 20, align: 'left'  },
      { key: 'H. Ini',     w: 16, align: 'right' },
      { key: 'H. Fin',     w: 16, align: 'right' },
      { key: 'Hs',         w: 14, align: 'right' },
      { key: 'Finalidad',  w: 30, align: 'left'  },
      { key: 'Instructor', w: 36, align: 'left'  },
      { key: 'At.',        w: 10, align: 'right' },
      { key: 'Forma pago', w: 28, align: 'left'  },
      { key: 'Aeroclub',   w: 37, align: 'left'  },
    ];
    const marginX = 12;
    let xCursor = marginX;
    const colsX = colsDef.map(c => {
      const x = xCursor;
      xCursor += c.w;
      return x;
    });
    const tableWidth = colsDef.reduce((a, c) => a + c.w, 0);

    let y = 46;
    // Cabecera de tabla
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(226, 232, 240);
    doc.rect(marginX, y - 5, tableWidth, 8, 'FD');
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...GRAY);
    colsDef.forEach((c, i) => {
      const xPad = c.align === 'right' ? colsX[i] + c.w - 2 : colsX[i] + 2;
      doc.text(c.key, xPad, y, { align: c.align });
    });
    y += 5;

    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);

    const trunc = (s, maxW, fontSize = 8) => {
      if (s == null) return '';
      let str = String(s);
      doc.setFontSize(fontSize);
      if (doc.getTextWidth(str) <= maxW) return str;
      while (str.length > 1 && doc.getTextWidth(str + '…') > maxW) str = str.slice(0, -1);
      return str + '…';
    };

    myClosedEntries.forEach((e, i) => {
      if (y > pageHeight - 22) {
        doc.addPage();
        y = 18;
        // Repetir cabecera en nueva página
        doc.setFillColor(241, 245, 249);
        doc.setDrawColor(226, 232, 240);
        doc.rect(marginX, y - 5, tableWidth, 8, 'FD');
        doc.setFont(undefined, 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(...GRAY);
        colsDef.forEach((c, ci) => {
          const xPad = c.align === 'right' ? colsX[ci] + c.w - 2 : colsX[ci] + 2;
          doc.text(c.key, xPad, y, { align: c.align });
        });
        y += 5;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(15, 23, 42);
      }
      // Zebra
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(marginX, y - 4, tableWidth, 7, 'F');
      }
      const fecha = new Date(e.flight_date).toLocaleDateString('es-AR');
      const aeroclub = e.organization_name || e.club_name || '';
      const values = [
        String(i + 1),
        fecha,
        e.aircraft || '',
        e.registration || '',
        e.hobbs_start != null ? String(e.hobbs_start) : '—',
        e.hobbs_end   != null ? String(e.hobbs_end)   : '—',
        e.hours_flown != null ? String(e.hours_flown) : '—',
        purposeLabel(e),
        instructorLabel(e),
        e.landings != null ? String(e.landings) : '—',
        e.payment_method || '—',
        aeroclub || '—',
      ];
      values.forEach((val, ci) => {
        const c = colsDef[ci];
        const text = trunc(val, c.w - 4);
        const xPad = c.align === 'right' ? colsX[ci] + c.w - 2 : colsX[ci] + 2;
        doc.text(text, xPad, y, { align: c.align });
      });
      y += 6;
    });

    // ── Total alineado bajo columna Hs ─────────────────────────
    // Si el total no entra cómodo arriba del descargo, paginamos antes
    // para garantizar aire entre el TOTAL, el descargo y el pie navy.
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }
    const totalLabelX = colsX[6] - 2;
    const totalValueX = colsX[6] + colsDef[6].w - 2;
    y += 4;
    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, y, marginX + tableWidth, y);
    y += 7;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...BLUE);
    doc.text(`TOTAL — ${myClosedEntries.length} vuelos`, totalLabelX, y, { align: 'right' });
    doc.text(`${myHours.toFixed(1)} hs`, totalValueX, y, { align: 'right' });

    // ── Footer disclaimer + pie navy ───────────────────────────
    // Disclaimer subido a -20 para dejar aire al TOTAL y al pie navy
    // (navy ocupa los últimos 10mm; el descargo a 7pt puede wrappear a 2 líneas).
    const pageCount = doc.getNumberOfPages();
    const disclaimer = 'Documento generado por Condor360 — herramienta de asistencia administrativa. No reemplaza el libro de vuelos oficial del piloto.';
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(disclaimer, pageWidth / 2, pageHeight - 20, { align: 'center', maxWidth: pageWidth - 24 });
      doc.setFillColor(...NAVY);
      doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
      doc.setTextColor(147, 197, 253);
      doc.setFontSize(7);
      doc.text(`${orgName} · ${monthLabel}`, 14, pageHeight - 4);
      doc.text(`${pilotName} · pág. ${p}/${pageCount}`, pageWidth - 14, pageHeight - 4, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);

    doc.save(`libro-vuelos-${pilotName.replace(/\s/g, '-')}-${year}-${String(month).padStart(2, '0')}.pdf`);
  };

  const handleExportExcel = () => {
    if (!myClosedEntries.length) return;
    const pilotName = currentUser?.name || 'Piloto';
    const rows = myClosedEntries.map((e, i) => ({
      '#': i + 1,
      Fecha: new Date(e.flight_date).toLocaleDateString('es-AR'),
      Aeronave: e.aircraft || '',
      Matrícula: e.registration || '',
      'Horómetro Inicio': e.hobbs_start,
      'Horómetro Fin': e.hobbs_end,
      Horas: parseFloat(e.hours_flown),
      Finalidad: purposeLabel(e),
      Instructor: instructorLabel(e),
      Aterrizajes: e.landings != null ? e.landings : '',
      'Forma de pago': e.payment_method || '',
      Club: e.club_name || '',
    }));
    rows.push({
      '#': '', Fecha: '', Aeronave: '', Matrícula: '',
      'Horómetro Inicio': '', 'Horómetro Fin': 'TOTAL',
      Horas: myHours,
      Finalidad: '', Instructor: '', Aterrizajes: '', 'Forma de pago': '',
      Club: '',
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 4 }, { wch: 12 }, { wch: 22 }, { wch: 12 },
      { wch: 16 }, { wch: 16 }, { wch: 8 },
      { wch: 18 }, { wch: 22 }, { wch: 12 }, { wch: 18 },
      { wch: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${MONTHS[month-1]} ${year}`);
    XLSX.writeFile(wb, `libro-vuelos-${pilotName.replace(/\s/g, '-')}-${year}-${String(month).padStart(2, '0')}.xlsx`);
  };

  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:'fixed', inset:0, zIndex:60, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={onClose}>
      <motion.div
        initial={{ scale:0.95, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.95, y:20 }}
        onClick={e => e.stopPropagation()}
        style={{ width:'100%', maxWidth:'600px', maxHeight:'88vh', display:'flex', flexDirection:'column', borderRadius:'24px', overflow:'hidden', background:darkMode?'#111827':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px 16px', borderBottom:darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:38, height:38, borderRadius:12, background:'linear-gradient(135deg,#16a34a,#15803d)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <BookOpen style={{ width:18, height:18, color:'white' }} />
            </div>
            <div>
              <h2 style={{ margin:0, fontSize:17, fontWeight:800, color:text }}>Mis Vuelos</h2>
              <p style={{ margin:0, fontSize:12, color:sub }}>
                {loading ? 'Cargando...' : `${myClosedEntries.length} vuelos · ${myHours.toFixed(1)} hs propias · ${totalHours.toFixed(1)} hs totales`}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background:darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', border:'none', borderRadius:10, width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:sub }}>
            <X style={{ width:16, height:16 }} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, padding:'10px 24px 0', flexShrink:0 }}>
          {[
            { id: 'flights',    label: 'Vuelos',         icon: BookOpen  },
            { id: 'stats',      label: t('statistics',{ns:'admin'}),   icon: BarChart3 },
            { id: 'experience', label: 'Mi Experiencia', icon: Star      },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                flex:1, padding:'8px 6px', borderRadius:'12px 12px 0 0', border:'none', cursor:'pointer',
                fontFamily:'inherit', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:5,
                background: tab === t.id
                  ? (t.id === 'experience'
                      ? (darkMode ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)')
                      : (darkMode ? 'rgba(22,163,74,0.15)' : 'rgba(22,163,74,0.08)'))
                  : 'transparent',
                color: tab === t.id ? (t.id === 'experience' ? '#8b5cf6' : '#16a34a') : sub,
                borderBottom: tab === t.id
                  ? `2px solid ${t.id === 'experience' ? '#8b5cf6' : '#16a34a'}`
                  : '2px solid transparent',
              }}>
              <t.icon style={{ width:13, height:13 }} /> {t.label}
            </button>
          ))}
        </div>

        {/* Nav month — solo visible en flights y stats */}
        {tab !== 'experience' && (
          <div style={{ padding:'10px 24px', borderBottom:darkMode?'1px solid rgba(255,255,255,0.07)':'1px solid #f1f5f9', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ padding:'10px 14px', borderRadius:14, background:darkMode?'rgba(22,163,74,0.12)':'rgba(220,252,231,0.8)', border:darkMode?'1px solid rgba(22,163,74,0.25)':'1px solid rgba(22,163,74,0.15)' }}>
                  <p style={{ margin:0, fontSize:20, fontWeight:900, color:darkMode?'#4ade80':'#16a34a', lineHeight:1 }}>{myClosedEntries.length}</p>
                  <p style={{ margin:0, fontSize:10, fontWeight:600, color:darkMode?'rgba(74,222,128,0.6)':'#22c55e' }}>Vuelos</p>
                </div>
                <div style={{ padding:'10px 14px', borderRadius:14, background:darkMode?'rgba(37,99,235,0.12)':'rgba(219,234,254,0.8)', border:darkMode?'1px solid rgba(37,99,235,0.25)':'1px solid rgba(37,99,235,0.15)' }}>
                  <p style={{ margin:0, fontSize:20, fontWeight:900, color:darkMode?'#93c5fd':'#2563eb', lineHeight:1 }}>{myHours.toFixed(1)}</p>
                  <p style={{ margin:0, fontSize:10, fontWeight:600, color:darkMode?'rgba(147,197,253,0.6)':'#3b82f6' }}>Horas</p>
                </div>
              </div>
              <PeriodPicker
                month={month} year={year}
                onMonthChange={setMonth} onYearChange={setYear}
                onPrev={prevMonth} onNext={nextMonth}
                isCurrentMonth={isCurrent}
                darkMode={darkMode}
                compact
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ overflowY:'auto', flex:1, padding:'16px 24px' }}>

          {/* ── Tab Vuelos ── */}
          {tab === 'flights' && (
            <>
              {/* PR #6 — Saldo de paquete de horas (visible si el alumno tiene paquetes) */}
              {pkgBalance && (pkgBalance.active_packages > 0 || pkgBalance.total_balance !== 0) && (() => {
                const bal      = pkgBalance.active_balance;
                const isNeg    = bal < 0;
                const isZero   = bal === 0;
                const accent   = isNeg ? '#ef4444' : isZero ? '#94a3b8' : '#16a34a';
                const bgGrad   = isNeg
                  ? 'linear-gradient(135deg,rgba(239,68,68,0.12),rgba(239,68,68,0.05))'
                  : 'linear-gradient(135deg,rgba(22,163,74,0.10),rgba(22,163,74,0.04))';
                return (
                  <div style={{
                    marginBottom: 12,
                    borderRadius: 16, padding: '14px 16px',
                    background: bgGrad,
                    border: `1px solid ${accent}33`,
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      background: `${accent}22`, color: accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {isNeg ? <AlertTriangle style={{ width: 18, height: 18 }} /> : <Wallet style={{ width: 18, height: 18 }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: sub }}>
                        Paquete de horas
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: isNeg ? accent : sub }}>
                        {isNeg
                          ? `Saldo negativo — contactá a tu instructor`
                          : isZero
                            ? 'Sin horas disponibles — cargá un paquete con tu admin'
                            : `${pkgBalance.active_packages} ${pkgBalance.active_packages === 1 ? 'paquete activo' : 'paquetes activos'}`}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1 }}>
                        {bal}
                      </p>
                      <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: accent, opacity: 0.7 }}>
                        hs disponibles
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* PR #6 (Vision) — toggle para ocultar vuelos importados */}
              {(data?.entries || []).some(e => e.import_id) && (
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
                  <button onClick={() => setShowImported(v => !v)}
                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:99, border:'1px solid rgba(124,58,237,0.3)', background: showImported ? 'rgba(124,58,237,0.1)' : 'transparent', color:'#7c3aed', cursor:'pointer', fontWeight:700, fontSize:11, fontFamily:'inherit' }}>
                    <UploadIcon style={{ width:11, height:11 }} />
                    {showImported ? 'Ocultar importados' : 'Mostrar importados'}
                  </button>
                </div>
              )}

              {loading ? (
                <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
                  <Loader style={{ width:28, height:28, color:'#16a34a', animation:'spin 1s linear infinite' }} />
                </div>
              ) : myEntries.length === 0 ? (
                <div style={{ textAlign:'center', padding:'48px 0' }}>
                  <BookOpen style={{ width:40, height:40, margin:'0 auto 12px', color:'#94a3b8' }} />
                  <p style={{ color:'#94a3b8', fontSize:14, fontWeight:600 }}>Sin vuelos en {MONTHS[month-1]} {year}</p>
                  <p style={{ color:darkMode?'rgba(255,255,255,0.2)':'#cbd5e1', fontSize:12, marginTop:4 }}>
                    Los vuelos se registran al completar un chequeo con horómetro
                  </p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {myEntries.map((entry, i) => {
                    const st = entry.status ?? 'closed';
                    const isOpen     = st === 'open';
                    const isAnnulled = st === 'annulled';
                    const bgGrad   = isAnnulled
                      ? 'linear-gradient(135deg,#94a3b8,#64748b)'
                      : isOpen
                        ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                        : 'linear-gradient(135deg,#16a34a,#15803d)';
                    const hoursColor = isAnnulled ? '#94a3b8' : isOpen ? '#f59e0b' : '#16a34a';
                    return (
                    <motion.div key={entry.id}
                      initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.04 }}
                      style={{
                        borderRadius:16,
                        border: isAnnulled
                          ? '1px solid rgba(148,163,184,0.45)'
                          : isOpen
                            ? '1px solid rgba(245,158,11,0.45)'
                            : (darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0'),
                        background: isAnnulled
                          ? (darkMode?'rgba(148,163,184,0.08)':'rgba(248,250,252,0.9)')
                          : (darkMode?'rgba(255,255,255,0.03)':'white'),
                        padding:'14px 16px', display:'flex', alignItems:'center', gap:14,
                        opacity: isAnnulled ? 0.7 : 1,
                      }}>
                      <div style={{ width:36, height:36, borderRadius:12, background:bgGrad, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'white', fontWeight:800, fontSize:13 }}>
                        {i+1}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <span style={{ fontWeight:700, fontSize:14, color:text, textDecoration: isAnnulled ? 'line-through' : 'none' }}>{entry.aircraft}</span>
                          {entry.registration && (
                            <span style={{ fontSize:11, fontFamily:'monospace', padding:'1px 8px', borderRadius:99, background:darkMode?'rgba(255,255,255,0.1)':'#f1f5f9', color:darkMode?'#94a3b8':'#64748b' }}>{entry.registration}</span>
                          )}
                          {isOpen && (
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:0.5, padding:'2px 8px', borderRadius:99, background:'rgba(245,158,11,0.15)', color:'#d97706', border:'1px solid rgba(245,158,11,0.35)' }}>SIN CERRAR</span>
                          )}
                          {isAnnulled && (
                            <span style={{ fontSize:10, fontWeight:800, letterSpacing:0.5, padding:'2px 8px', borderRadius:99, background:'rgba(148,163,184,0.15)', color:'#64748b', border:'1px solid rgba(148,163,184,0.4)' }}>ANULADO</span>
                          )}
                          {entry.import_id && (
                            <span title="Vuelo digitalizado de una planilla histórica"
                              style={{ fontSize:10, fontWeight:800, letterSpacing:0.5, padding:'2px 8px', borderRadius:99, background:'rgba(124,58,237,0.12)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.35)', display:'inline-flex', alignItems:'center', gap:4 }}>
                              <UploadIcon style={{ width:9, height:9 }} /> IMPORTADO
                            </span>
                          )}
                        </div>
                        <div style={{ display:'flex', gap:12, marginTop:4, fontSize:12, color:sub, flexWrap:'wrap' }}>
                          <span>{new Date(entry.flight_date).toLocaleDateString('es-AR')} {entry.flight_time && `· ${entry.flight_time}`}</span>
                          <span>{entry.hobbs_start ?? '—'} → {entry.hobbs_end ?? '—'}</span>
                          {entry.club_name && <span>{entry.club_name}</span>}
                        </div>
                        {/* PR #5 — Libro de vuelo (finalidad, instructor, aterrizajes, pago) */}
                        {(entry.purpose || entry.instructor_name || entry.landings != null || entry.payment_method) && (
                          <div style={{ display:'flex', gap:10, marginTop:4, fontSize:11, color:sub, flexWrap:'wrap' }}>
                            {entry.purpose && <span>🎯 {purposeLabel(entry)}</span>}
                            {entry.instructor_name && <span>🎓 {entry.instructor_name}</span>}
                            {entry.landings != null && <span>🛬 {entry.landings} at.</span>}
                            {entry.payment_method && <span>💳 {entry.payment_method}</span>}
                          </div>
                        )}
                        {isAnnulled && entry.annulled_reason && (
                          <div style={{ marginTop:4, fontSize:11, color:darkMode?'#94a3b8':'#64748b', fontStyle:'italic' }}>
                            Motivo: {entry.annulled_reason}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <p style={{ margin:0, fontSize:18, fontWeight:900, color:hoursColor, lineHeight:1 }}>
                          {entry.hours_flown != null ? entry.hours_flown : '—'}
                        </p>
                        <p style={{ margin:0, fontSize:10, fontWeight:600, color:hoursColor, opacity:0.6 }}>hs</p>
                      </div>
                    </motion.div>
                    );
                  })}
                  <div style={{ borderRadius:16, background:'linear-gradient(135deg,rgba(22,163,74,0.08),rgba(21,128,61,0.04))', border:'1px solid rgba(22,163,74,0.2)', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, fontWeight:700, color:darkMode?'rgba(74,222,128,0.8)':'#16a34a' }}>Total {MONTHS[month-1]} {year}</span>
                    <span style={{ fontSize:18, fontWeight:900, color:'#16a34a' }}>{myHours.toFixed(1)} hs · {myClosedEntries.length} vuelos</span>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={handleExportPDF}
                      style={{ flex:1, padding:'10px', borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, color:sub, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                      <FileText style={{ width:14, height:14 }} /> PDF
                    </button>
                    <button onClick={handleExportExcel}
                      style={{ flex:1, padding:'10px', borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, color:sub, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                      <Table2 style={{ width:14, height:14 }} /> Excel
                    </button>
                    <button onClick={handleExportCSV}
                      style={{ flex:1, padding:'10px', borderRadius:14, border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, color:sub, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                      <FileDown style={{ width:14, height:14 }} /> CSV
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Tab Estadísticas ── */}
          {tab === 'stats' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {stats && (
                <div style={{ borderRadius:16, background:'linear-gradient(135deg,rgba(22,163,74,0.1),rgba(37,99,235,0.08))', border:'1px solid rgba(22,163,74,0.2)', padding:'18px 20px' }}>
                  <p style={{ margin:'0 0 12px', fontSize:14, fontWeight:800, color:text }}>Acumulado {year}</p>
                  <div style={{ display:'flex', gap:16 }}>
                    <div style={{ flex:1, textAlign:'center' }}>
                      <p style={{ margin:0, fontSize:28, fontWeight:900, color:'#16a34a', lineHeight:1 }}>{stats.annual.flights}</p>
                      <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:600, color:sub }}>Vuelos</p>
                    </div>
                    <div style={{ width:1, background:darkMode?'rgba(255,255,255,0.1)':'#e2e8f0' }} />
                    <div style={{ flex:1, textAlign:'center' }}>
                      <p style={{ margin:0, fontSize:28, fontWeight:900, color:'#2563eb', lineHeight:1 }}>{stats.annual.hours}</p>
                      <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:600, color:sub }}>Horas</p>
                    </div>
                  </div>
                </div>
              )}

              {stats && (
                <div style={{ borderRadius:16, border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0', padding:'18px 20px', background:darkMode?'rgba(255,255,255,0.03)':'white' }}>
                  <p style={{ margin:'0 0 14px', fontSize:14, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:6 }}>
                    <BarChart3 style={{ width:16, height:16, color:'#16a34a' }} /> Horas por mes — {year}
                  </p>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:120 }}>
                    {stats.monthly.map((m, i) => {
                      const pct = maxBarHours > 0 ? (m.hours / maxBarHours) * 100 : 0;
                      const isCurrentMonth = i + 1 === month;
                      return (
                        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                          {m.hours > 0 && (
                            <span style={{ fontSize:9, fontWeight:700, color:darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>{m.hours}</span>
                          )}
                          <motion.div
                            initial={{ height:0 }} animate={{ height:`${Math.max(pct, m.hours > 0 ? 4 : 0)}%` }}
                            transition={{ delay: i * 0.05, duration: 0.4 }}
                            style={{ width:'100%', minHeight: m.hours > 0 ? 4 : 0, borderRadius:'6px 6px 0 0', background: isCurrentMonth ? 'linear-gradient(180deg,#16a34a,#15803d)' : darkMode ? 'rgba(22,163,74,0.3)' : 'rgba(22,163,74,0.2)' }}
                          />
                          <span style={{ fontSize:9, fontWeight:600, color: isCurrentMonth ? '#16a34a' : sub }}>{MONTHS_SHORT[i]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {stats?.career && (
                <div style={{ borderRadius:16, background:'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(59,130,246,0.06))', border:'1px solid rgba(139,92,246,0.2)', padding:'18px 20px' }}>
                  <p style={{ margin:'0 0 12px', fontSize:14, fontWeight:800, color:text, display:'flex', alignItems:'center', gap:6 }}>
                    <Award style={{ width:16, height:16, color:'#8b5cf6' }} /> Total de carrera
                  </p>
                  <div style={{ display:'flex', gap:16 }}>
                    <div style={{ flex:1, textAlign:'center' }}>
                      <p style={{ margin:0, fontSize:28, fontWeight:900, color:'#8b5cf6', lineHeight:1 }}>{stats.career.total_flights}</p>
                      <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:600, color:sub }}>Vuelos Condor360</p>
                    </div>
                    <div style={{ width:1, background:darkMode?'rgba(255,255,255,0.1)':'#e2e8f0' }} />
                    <div style={{ flex:1, textAlign:'center' }}>
                      <p style={{ margin:0, fontSize:28, fontWeight:900, color:'#6d28d9', lineHeight:1 }}>{totalHours.toFixed(1)}</p>
                      <p style={{ margin:'4px 0 0', fontSize:11, fontWeight:600, color:sub }}>Horas totales</p>
                    </div>
                  </div>
                  {stats.career.first_flight && (
                    <p style={{ margin:'12px 0 0', fontSize:11, color:sub, textAlign:'center' }}>
                      Primer vuelo registrado: {new Date(stats.career.first_flight).toLocaleDateString('es-AR')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tab Mi Experiencia ── */}
          {tab === 'experience' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* Explicación */}
              <div style={{ borderRadius:16, background: darkMode ? 'rgba(139,92,246,0.1)' : 'rgba(237,233,254,0.8)', border:'1px solid rgba(139,92,246,0.2)', padding:'18px 20px' }}>
                <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#7c3aed,#6d28d9)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Star style={{ width:18, height:18, color:'white' }} />
                  </div>
                  <div>
                    <p style={{ margin:'0 0 4px', fontSize:14, fontWeight:800, color: darkMode ? 'white' : '#4c1d95' }}>
                      ¿Venís de otra escuela o aeroclub?
                    </p>
                    <p style={{ margin:0, fontSize:13, lineHeight:1.5, color: darkMode ? 'rgba(255,255,255,0.55)' : '#6d28d9' }}>
                      Cargá tus horas de vuelo previas a Condor360 para tener tu historial completo en un solo lugar. Solo necesitás el total acumulado de tu libreta de vuelo.
                    </p>
                  </div>
                </div>
              </div>

              {/* Card horas previas */}
              <div style={{ borderRadius:16, border:darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0', background:darkMode?'rgba(255,255,255,0.03)':'white', padding:'20px' }}>
                <p style={{ margin:'0 0 16px', fontSize:13, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em', color:sub }}>
                  Horas previas a Condor360
                </p>

                {editingPrev ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <p style={{ margin:0, fontSize:13, color:sub }}>
                      Ingresá el total de horas de tu libreta de vuelo anterior:
                    </p>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <input
                        type="number" min="0" step="0.1" value={prevInput}
                        onChange={e => { setPrevInput(e.target.value); setSaveError(''); }}
                        placeholder="Ej: 250.5"
                        autoFocus
                        style={{ flex:1, padding:'10px 14px', borderRadius:12, border:darkMode?'1px solid rgba(139,92,246,0.4)':'1px solid #c4b5fd', background:darkMode?'rgba(255,255,255,0.06)':'#faf5ff', color:text, fontSize:15, fontFamily:'inherit', outline:'none' }}
                      />
                      <span style={{ fontSize:14, fontWeight:700, color:sub }}>hs</span>
                    </div>

                    {/* Error visible en UI */}
                    {saveError && (
                      <p style={{ margin:0, fontSize:12, color:'#ef4444', fontWeight:600 }}>
                        ⚠️ {saveError}
                      </p>
                    )}

                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={savePrevHours} disabled={savingPrev}
                        style={{ flex:1, padding:'11px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#7c3aed,#6d28d9)', color:'white', fontWeight:700, fontSize:13, cursor: savingPrev ? 'not-allowed' : 'pointer', fontFamily:'inherit', opacity: savingPrev ? 0.7 : 1 }}>
                        {savingPrev ? 'Guardando...' : '✓ Guardar horas'}
                      </button>
                      <button onClick={() => { setEditingPrev(false); setSaveError(''); }}
                        style={{ padding:'11px 16px', borderRadius:12, border:darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', color:sub, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                    <div>
                      <p style={{ margin:0, fontSize:36, fontWeight:900, color:'#7c3aed', lineHeight:1 }}>
                        {prevHours.toFixed(1)}
                        <span style={{ fontSize:16, fontWeight:600, color:sub, marginLeft:6 }}>hs</span>
                      </p>
                      <p style={{ margin:'4px 0 0', fontSize:12, color:sub }}>
                        {prevHours > 0 ? t('previous_hours',{ns:'admin'}) : t('no_previous_hours',{ns:'admin'})}
                      </p>
                    </div>
                    <button onClick={() => { setPrevInput(String(prevHours)); setEditingPrev(true); setSavedOk(false); setSaveError(''); }}
                      style={{ padding:'9px 16px', borderRadius:12, border:'1px solid rgba(139,92,246,0.3)', background: darkMode ? 'rgba(139,92,246,0.12)' : 'rgba(237,233,254,0.8)', color:'#7c3aed', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                      ✏️ {prevHours > 0 ? 'Editar' : 'Cargar horas'}
                    </button>
                  </div>
                )}

                {savedOk && (
                  <motion.p initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }}
                    style={{ margin:'12px 0 0', fontSize:13, fontWeight:600, color:'#16a34a', textAlign:'center' }}>
                    ✅ Horas guardadas correctamente
                  </motion.p>
                )}
              </div>

              {/* Resumen total — 3 columnas alineadas */}
              <div style={{ borderRadius:16, background:'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(59,130,246,0.06))', border:'1px solid rgba(139,92,246,0.2)', padding:'18px 20px' }}>
                <p style={{ margin:'0 0 14px', fontSize:13, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em', color:sub }}>
                  Tu experiencia total
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {[
                    { value: prevHours.toFixed(1),                                    label: 'Horas previas',   color: '#8b5cf6', highlight: false },
                    { value: parseFloat(stats?.career?.total_hours || 0).toFixed(1),  label: 'Horas Condor360', color: '#16a34a', highlight: false },
                    { value: totalHours.toFixed(1),                                   label: 'Total carrera',   color: '#6d28d9', highlight: true  },
                  ].map((item, i) => (
                    <div key={i} style={{
                      textAlign:'center', padding:'12px 8px', borderRadius:12,
                      background: item.highlight
                        ? (darkMode ? 'rgba(139,92,246,0.18)' : 'rgba(237,233,254,0.8)')
                        : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                      border: item.highlight
                        ? '1px solid rgba(139,92,246,0.3)'
                        : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9'),
                    }}>
                      <p style={{ margin:0, fontSize:22, fontWeight:900, color:item.color, lineHeight:1 }}>{item.value}</p>
                      <p style={{ margin:'5px 0 0', fontSize:10, fontWeight: item.highlight ? 700 : 600, color: item.highlight ? '#7c3aed' : sub }}>
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

        </div>
      </motion.div>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </motion.div>
  );
};

export default PilotFlightLog;
